import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { LeftSidebarListItem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { FolderApi } from '../../../api/folders/models/folder.api.model';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { BaseModel } from '../../../Common/Basic/models/base.model';
import { ChipModel } from '../../../Common/Forms/models/chip.model';

/** Stores mentions, also referred to as saved coverage */
export class Folder extends BaseModel<Folder, FolderApi> implements AuditableModel, LeftSidebarListItemConvertableModel {
    id: number;
    name: string;

    totalMentions?: number;

    createdByUserId?: number;
    createdDate?: Date;

    modifiedByUserId?: number;
    modifiedDate?: Date;

    hidden?: boolean;
    groupId: number;
    isFavorite: boolean;

    constructor(apiModel?: FolderApi) {
        super();
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApiModel(apiModel: FolderApi) {
        this.id = apiModel.id;
        this.name = apiModel.name;
        this.totalMentions = apiModel.totalMentions;
        this.createdByUserId = apiModel.createdByUserId;
        this.createdDate = new Date(apiModel.createdDate);
        this.modifiedByUserId = apiModel.lastModifiedByUserId;
        this.modifiedDate = apiModel.lastModifiedDate ? new Date(apiModel.lastModifiedDate) : null;
        this.hidden = apiModel.hidden;
        this.groupId = apiModel.displayGroupId;
        this.isFavorite = apiModel.isFavorited;
    }

    constructDefault() {
        this.isFavorite = false;
    }

    toApiModel(): FolderApi {
        return {
            id: this.id,
            name: this.name,
            totalMentions: this.totalMentions,
            createdByUserId: this.createdByUserId,
            createdDate: this.createdDate ? +this.createdDate : null,
            lastModifiedByUserId: this.modifiedByUserId,
            lastModifiedDate: this.modifiedDate ? +this.modifiedDate : null,
            hidden: this.hidden,
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite
        };
    }

    toChipModel(): ChipModel {
        return {
            id: this.id,
            label: this.name
        };
    }

    toLeftSidebarListItem(buttons?: LeftSidebarListItemButton[]): LeftSidebarListItem {
        const item = new LeftSidebarListItem();

        item.value = this.id;
        item.label = this.name;
        // Use last modified date if it has a value, if not use created date
        item.lastModifiedDate = +((this.modifiedDate) ? this.modifiedDate : this.createdDate);
        item.status = this.totalMentions;

        if (this.hidden) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.hide);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.show);
        }

        if (this.isFavorite) {
            buttons = buttons.filter(button => button.key !== ActionsEnum.favorite);
        } else {
            buttons = buttons.filter(button => button.key !== ActionsEnum.unfavorite);
        }

        item.buttons = buttons;
        item.groupId = this.groupId;
        item.isFavorite = this.isFavorite;

        return item;
    }

    clone(): Folder {
        return new Folder(this.toApiModel());
    }

    equals(other?: Folder): boolean {
        if (!other) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (this.totalMentions !== other.totalMentions) {
            return false;
        }

        if (this.createdByUserId !== other.createdByUserId) {
            return false;
        }

        if (this.createdDate !== other.createdDate) {
            return false;
        }

        if (this.modifiedByUserId !== other.modifiedByUserId) {
            return false;
        }

        if (this.modifiedDate !== other.modifiedDate) {
            return false;
        }

        if (this.groupId !== other?.groupId) {
            return false;
        }

        if (this.hidden !== other?.hidden) {
            return false;
        }

        if (this.isFavorite !== other.isFavorite) {
            return false;
        }

        return true;
    }
}
